import { useEffect, useRef } from "react";

/**
 * Runs the callback after a delay, and resets the delay if
 * the event is triggered again before the rest delay resolves.
 *
 * @template T - The type of arguments the callback accepts
 * @param callback - The function to debounce
 * @param delay - The delay in milliseconds
 * @returns A debounced version of the callback that accepts the same arguments
 */
function useDebouncedCallback<T extends any[]>(
  callback: (...args: T) => void,
  delay: number
): (...args: T) => void {
  const timeout = useRef<ReturnType<typeof setTimeout>>();

  function cleanUp(): void {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  }

  // make sure our timeout gets cleared if
  // our consuming component gets unmounted
  useEffect(() => cleanUp, []);

  return function debouncedCallback(...args: T) {
    // Clear debounce timer
    cleanUp();

    // Start delaying again
    timeout.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };
}

export default useDebouncedCallback;
